import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { IonLabel, IonSegment, IonSegmentButton } from '@ionic/react';
import { Wallet } from 'lucide-react';
import { formatMoney } from '@tcrd/shared';
import { Card, EmptyState, ErrorState, PaymentStatusBadge, Skeleton } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { api, errorMessage } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import type { PaymentDto } from './payments-types';

/** Cobros (conductor) o pagos y comprobantes (cliente). */
export function PaymentsListPage({ role }: { role: 'customer' | 'driver' }) {
  const [tab, setTab] = useState<'pending' | 'all'>(role === 'driver' ? 'pending' : 'all');
  const q = useQuery({ queryKey: ['payments', tab], queryFn: () => api<PaymentDto[]>('/payments', { query: { status: tab === 'pending' ? 'pending' : undefined } }) });
  const href = (p: PaymentDto) => (role === 'driver' ? `/driver/payments/${p.id}` : p.ride ? `/app/rides/${p.ride.id}` : `/app/payments`);

  return (
    <Page title={role === 'driver' ? 'Cobros' : 'Pagos y comprobantes'} back={role === 'driver' ? '/driver/more' : '/app/profile'} onRefresh={() => q.refetch()}>
      <IonSegment value={tab} onIonChange={(e) => setTab(e.detail.value as 'pending' | 'all')} className="mb-4">
        <IonSegmentButton value="pending"><IonLabel>Pendientes</IonLabel></IonSegmentButton>
        <IonSegmentButton value="all"><IonLabel>Todos</IonLabel></IonSegmentButton>
      </IonSegment>
      {q.isLoading && <Skeleton className="h-28" />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {q.data?.length === 0 && <EmptyState icon={<Wallet className="size-6" />} title={tab === 'pending' ? 'Sin cobros pendientes' : 'Sin pagos'} />}
      <div className="flex flex-col gap-3">
        {q.data?.map((p) => (
          <Link key={p.id} to={href(p)}>
            <Card className="p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{p.ride?.code ?? 'Paquete'} · {role === 'driver' ? p.customer.name : p.driver.name}</span>
                <PaymentStatusBadge status={p.status} />
              </div>
              <p className="text-xs text-[var(--text-muted)]">{fmtDateTime(p.ride?.completedAt ?? p.ride?.scheduledAt ?? p.updatedAt)}</p>
              <div className="mt-2 flex justify-between text-sm">
                <span>Tarifa {formatMoney(p.totalDue)}{Number(p.tipTotal) > 0 ? ` · propina ${formatMoney(p.tipTotal)}` : ''}</span>
                <span className="font-semibold">Saldo {formatMoney(p.balance)}</span>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </Page>
  );
}
